// V2 版式：duo-compare（前端 DOM 版）——对照后端 v2/layouts/duo-compare.ts 逐坐标移植
// 中央 1px 竖 hairline 贯底；左右两栏：小标题 + 最多 4 条要点（小方块引导）；
// 右栏小标题用 accent（「我们 / After」一方）。供料 compare block，缺省降级 bullets 对半拆。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { isLuxe } from "./luxe";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

// 与后端 duo-compare.ts GEO 同源（v2geo.generated.ts 由 deno dump 生成）
const GEO = V2GEO["duo-compare"] as any;

export default function V2DuoCompare({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const W = 13.333;
  // luxe：右栏（accent 一方）金条标题装饰 + 裸金引导方块（duo-compare.ts 分支）
  const luxe = isLuxe(skin);
  // 前端 Block 类型尚未含 V2 block（slides.ts 待主代理同步），此处按后端 types.ts 结构取用
  const blocks = c.blocks as any[];
  const ci = blocks.findIndex((b) => b.type === "compare");
  const bi = blocks.findIndex((b) => b.type === "bullets");

  // 供料解析 + 编辑路径（compare 用 blocks.ci.left/right.*；降级 bullets 对半拆，标题为静态文案）
  let left: { title: string; items: string[] };
  let right: { title: string; items: string[] };
  let leftTitlePath: string | null = null, rightTitlePath: string | null = null;
  const itemPath = (side: "left" | "right", i: number) =>
    ci >= 0 ? `blocks.${ci}.${side}.items.${i}` : null;
  if (ci >= 0) {
    const cmp = blocks[ci];
    left = cmp.left; right = cmp.right;
    leftTitlePath = `blocks.${ci}.left.title`;
    rightTitlePath = `blocks.${ci}.right.title`;
  } else {
    const items: string[] = bi >= 0 ? blocks[bi].items : [];
    const half = Math.ceil(items.length / 2);
    left = { title: "现状", items: items.slice(0, half) };
    right = { title: "目标", items: items.slice(half) };
  }
  const bulletsItemPath = (i: number) => (bi >= 0 ? `blocks.${bi}.items.${i}` : null);

  const renderCol = (colX: number, head: string, items: string[], isAccent: boolean, side: "left" | "right", headPath: string | null) => (
    <div key={side}>
      {/* luxe 金条标题装饰（accent 细条，仅 accent 栏） */}
      {luxe && isAccent && (
        <div style={{
          position: "absolute", left: px(colX), top: px(GEO.headY - 0.14),
          width: px(0.42), height: px(0.035), background: skin.accent,
        }} />
      )}
      {/* 栏小标题（右栏 accent 上色） */}
      {headPath ? (
        <EditableText
          value={head} path={headPath} editable={editable}
          onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
          style={{
            position: "absolute", left: px(colX), top: px(GEO.headY), width: px(GEO.colW), height: px(GEO.headH),
            fontSize: pt(skin.scale.sectionHead), color: isAccent ? skin.accent : skin.text,
            fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400,
            display: "flex", alignItems: "center",
          }}
        />
      ) : (
        <div style={{
          position: "absolute", left: px(colX), top: px(GEO.headY), width: px(GEO.colW), height: px(GEO.headH),
          fontSize: pt(skin.scale.sectionHead), color: isAccent ? skin.accent : skin.text,
          fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400,
          display: "flex", alignItems: "center",
        }}>{head}</div>
      )}
      {items.slice(0, GEO.maxItems).map((t, i) => {
        const y = GEO.itemY0 + i * GEO.itemH;
        const p = ci >= 0 ? itemPath(side, i) : bulletsItemPath(side === "left" ? i : i + Math.ceil((blocks[bi]?.items?.length ?? 0) / 2));
        return (
          <div key={i}>
            {/* 引导小方块（静态装饰；luxe accent 栏用裸金） */}
            <div style={{
              position: "absolute", left: px(colX), top: px(y + 0.07),
              width: px(GEO.bullet), height: px(GEO.bullet),
              background: luxe && isAccent ? skin.accent : skin.text,
            }} />
            {p ? (
              <EditableText
                value={t} path={p} editable={editable} multiline
                onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                style={{
                  position: "absolute", left: px(colX + GEO.bulletPad), top: px(y),
                  width: px(GEO.colW - GEO.bulletPad), height: px(GEO.itemH - 0.1),
                  fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody,
                  lineHeight: 1.15,
                }}
              />
            ) : (
              <div style={{
                position: "absolute", left: px(colX + GEO.bulletPad), top: px(y),
                width: px(GEO.colW - GEO.bulletPad), height: px(GEO.itemH - 0.1),
                fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody,
                lineHeight: 1.15,
              }}>{t}</div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "duo-compare")} page={page} total={total} foot={footOf()} />
      {/* 页面大标题（长度分档） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(m), top: px(GEO.titleY), width: px(W - 2 * m), height: px(GEO.titleH),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400, lineHeight: 1.1,
        }}
      />
      {/* 中央 1px 竖 hairline */}
      <div style={{
        position: "absolute", left: px(GEO.divX), top: px(GEO.divTop),
        width: 1, height: px(GEO.divBottom - GEO.divTop), background: skin.hairline,
      }} />
      {renderCol(m, left.title, left.items, false, "left", leftTitlePath)}
      {renderCol(GEO.divX + GEO.colPad, right.title, right.items, true, "right", rightTitlePath)}
    </>
  );
}
